import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr } from "@/i18n/copy";
import PushTestButton from "@/components/PushTestButton";
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { enablePushNotifications, disablePushNotifications, getPushDeviceState, pushPlatformSupport } from "@/pwa";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function PushDeviceSettings({ relationshipId = null }) {
  useUiLanguage();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(window.Notification?.permission || "default");
  const support = pushPlatformSupport();
  const refresh = async () => {
    setPermission(window.Notification?.permission || "default");
    try { setStatus(await getPushDeviceState(api, relationshipId)); }
    catch { /* Keep the last confirmed state on a temporary network failure. */ }
  };
  useEffect(() => {
    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("tps:push-state-changed", refresh);
    return () => { window.removeEventListener("focus", refresh); window.removeEventListener("tps:push-state-changed", refresh); };
  }, [relationshipId]);
  const perform = async (action) => {
    setBusy(true);
    try {
      if (action === "enable") {
        if (!await enablePushNotifications(api, relationshipId)) throw new Error(tr("Attivazione non completata. Controlla il permesso notifiche e la configurazione dello Studio."));
        toast.success(tr("Notifiche Push attivate per il tuo account su questo dispositivo"));
      } else if (action === "disable") {
        await disablePushNotifications(api, relationshipId);
        toast.success(tr("Notifiche Push disattivate su questo dispositivo"));
      }
      await refresh();
    } catch (error) { toast.error(errMsg(error)); }
    finally { setBusy(false); }
  };
  return <Card className="mb-4 space-y-3 bg-card/60 p-5" data-testid="push-device-settings">
    <h2 className="font-semibold">{tr("Notifiche Push sul dispositivo")}</h2>
    <p className="text-sm text-muted-foreground">{tr("Attivale dall’app di ogni telefono o tablet su cui vuoi ricevere gli avvisi dello Studio, anche quando l’app non è aperta.")}</p>
    {status && <p className="text-xs text-muted-foreground">{status.sender_name} · {status.active_subscriptions || 0} {tr("dispositivi registrati per il tuo account")}</p>}
    {support.reason === "ios_home_screen_required" && <p className="text-sm">{tr("Su iPhone e iPad aggiungi l’app alla schermata Home e aprila dalla sua icona, poi torna qui per attivare le notifiche.")}</p>}
    {support.reason === "unsupported" && <p className="text-sm">{tr("Questo browser non supporta le notifiche Push.")}</p>}
    {permission === "denied" && <p className="text-sm">{tr("Le notifiche sono bloccate nelle impostazioni del dispositivo o del browser. Consenti le notifiche per questa app, poi riprova.")}</p>}
    {status && (!status.configured || !status.push_enabled) && <p className="text-sm">{tr("Lo Studio deve completare o abilitare la configurazione Push.")}</p>}
    {status?.device_active && <p className="text-sm text-emerald-600">{tr("Notifiche Push attive su questo dispositivo.")}</p>}
    <div className="flex flex-wrap gap-2">
      <Button disabled={busy || status?.device_active || !support.canEnable || permission === "denied" || !status?.configured || !status?.push_enabled} onClick={() => perform("enable")}>{status?.device_active ? tr("Notifiche già attive") : tr("Attiva su questo dispositivo")}</Button>
      <PushTestButton relationshipId={relationshipId} enabled={!busy && Boolean(status?.device_active)} />
      <Button variant="ghost" disabled={busy || !status?.device_active} onClick={() => perform("disable")}>{tr("Disattiva su questo dispositivo")}</Button>
    </div>
  </Card>;
}


